/**
 * 招待作成フォーム
 */
import type { FC } from "react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { ButtonUI } from "../../../components/Button";
import type {
  CreateInvitationInput,
  Invitation,
} from "../../../hooks/useInvitationsApi";

type InvitationFormProps = {
  onSubmit: (input: CreateInvitationInput) => Promise<Invitation>;
  isSubmitting: boolean;
};

type FormValues = {
  email: string;
  role: "admin" | "member";
  sendEmail: boolean;
};

export const InvitationForm: FC<InvitationFormProps> = ({
  onSubmit,
  isSubmitting,
}) => {
  const [createdInvitation, setCreatedInvitation] = useState<Invitation | null>(
    null,
  );
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { register, handleSubmit, reset, formState, watch } =
    useForm<FormValues>({
      defaultValues: {
        email: "",
        role: "member",
        sendEmail: false,
      },
    });

  const sendEmailValue = watch("sendEmail");

  const handleFormSubmit = async (values: FormValues) => {
    setError(null);
    setCopied(false);
    try {
      const invitation = await onSubmit({
        email: values.email,
        role: values.role,
        sendEmail: values.sendEmail,
      });
      setCreatedInvitation(invitation);
      reset();
    } catch (e) {
      setError(e instanceof Error ? e.message : "招待の作成に失敗しました");
    }
  };

  const handleCopyUrl = async () => {
    if (createdInvitation?.invitationUrl) {
      await navigator.clipboard.writeText(createdInvitation.invitationUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setCreatedInvitation(null);
    setCopied(false);
  };

  return (
    <div className="card bg-base-100 shadow-sm">
      <div className="card-body">
        <h3 className="card-title text-lg">新規招待</h3>

        {createdInvitation ? (
          <div className="space-y-4">
            <div className="alert alert-success">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="stroke-current shrink-0 h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                role="img"
                aria-label="成功アイコン"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>
                {createdInvitation.emailSent
                  ? "招待メールを送信しました"
                  : "招待を作成しました"}
              </span>
            </div>

            {createdInvitation.emailSent ? (
              <div className="bg-base-200 p-4 rounded-lg">
                <p className="text-sm text-base-content/70">
                  <strong>{createdInvitation.email}</strong>{" "}
                  宛に招待メールを送信しました。
                </p>
              </div>
            ) : (
              <div className="form-control">
                <label className="label">
                  <span className="label-text">招待URL</span>
                </label>
                <div className="join w-full">
                  <input
                    type="text"
                    readOnly
                    value={createdInvitation.invitationUrl}
                    className="input input-bordered join-item w-full font-mono text-sm"
                  />
                  <button
                    type="button"
                    className="btn btn-primary join-item"
                    onClick={handleCopyUrl}
                  >
                    {copied ? "コピー済み" : "コピー"}
                  </button>
                </div>
                <label className="label">
                  <span className="label-text-alt text-base-content/60">
                    このURLを招待したいユーザーに共有してください
                  </span>
                </label>
              </div>
            )}

            <div className="card-actions justify-end">
              <ButtonUI type="button" color="ghost" onClick={handleClose}>
                閉じる
              </ButtonUI>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
            <div className="form-control">
              <label className="label">
                <span className="label-text">メールアドレス</span>
                <span className="label-text-alt text-error">必須</span>
              </label>
              <input
                type="email"
                placeholder="example@company.com"
                className="input input-bordered"
                {...register("email", { required: true })}
              />
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text">ロール</span>
              </label>
              <div className="flex gap-4">
                <label className="label cursor-pointer gap-2">
                  <input
                    type="radio"
                    className="radio radio-primary"
                    value="member"
                    {...register("role")}
                  />
                  <span className="label-text">メンバー</span>
                </label>
                <label className="label cursor-pointer gap-2">
                  <input
                    type="radio"
                    className="radio radio-primary"
                    value="admin"
                    {...register("role")}
                  />
                  <span className="label-text">管理者</span>
                </label>
              </div>
            </div>

            <div className="divider my-2" />

            <div className="form-control">
              <label className="label">
                <span className="label-text">招待方法</span>
              </label>
              <label className="label cursor-pointer justify-start gap-3">
                <input
                  type="checkbox"
                  className="checkbox checkbox-primary"
                  {...register("sendEmail")}
                />
                <span className="label-text">
                  招待メールを送信する
                  <span className="text-base-content/50 text-sm ml-2">
                    (チェックしない場合はURLをコピーして共有)
                  </span>
                </span>
              </label>
            </div>

            {error && (
              <div className="alert alert-error">
                <span>{error}</span>
              </div>
            )}

            <div className="card-actions justify-end">
              <ButtonUI
                type="submit"
                color="primary"
                disabled={isSubmitting || !formState.isValid}
              >
                {isSubmitting ? (
                  <span className="loading loading-spinner loading-sm" />
                ) : sendEmailValue ? (
                  "招待メールを送信"
                ) : (
                  "招待を作成"
                )}
              </ButtonUI>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
